import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories, type Category, type Article } from '@/mocks/forum';

function CategorySchema({ category, pageArticles }: { category: Category; pageArticles: Article[] }) {
  const origin = window.location.origin + __BASE_PATH__;
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '爱情岛论坛',
        url: origin,
        description: '最新、最热的情感交流、恋爱话题与社区互动内容汇聚平台。',
      },
      {
        '@type': 'WebPage',
        name: `${category.name} - 爱情岛论坛`,
        url: `${origin}/category/${category.slug}`,
        description: category.description,
        isPartOf: { '@type': 'WebSite', name: '爱情岛论坛' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin },
          { '@type': 'ListItem', position: 2, name: category.name, item: `${origin}/category/${category.slug}` },
        ],
      },
      ...pageArticles.map((article, index) => ({
        '@type': 'Article' as const,
        headline: article.title,
        description: article.excerpt,
        url: `${origin}/article/${article.id}`,
        author: { '@type': 'Person' as const, name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        position: index + 1,
      })),
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [otherCategories, setOtherCategories] = useState<Category[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (slug) {
      const found = categories.find((c) => c.slug === slug);
      if (found) {
        setSelectedCategory(found);
        setFilteredArticles(articles.filter((a) => a.categoryId === found.id));
        setOtherCategories(categories.filter((c) => c.id !== found.id));
      }
    }
  }, [slug]);

  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">分类不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">未找到对应的分类，可能已被移除</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <CategorySchema category={selectedCategory} pageArticles={filteredArticles} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="pt-20 pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: selectedCategory.name },
            ]} />
            <div className="mt-6 flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-500">
                <i className={`${selectedCategory.icon} text-2xl text-background-50`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {selectedCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{selectedCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 mb-4">
                    <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类下暂无文章</p>
                </div>
              )}
            </div>

            <div className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-menu-line"></i> 其他分类
                </h3>
                <div className="flex flex-col gap-1">
                  {otherCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}